import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import axiosInstance from "../../../axiosInstance";
import { toast } from "react-toastify";
import "./Brands.scss";
import { useAuth } from "../../../contexts/AuthContext";

const Brands = ({ markPageAsCompleted }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupAction, setPopupAction] = useState(() => () => {});
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [initialValues, setInitialValues] = useState({
    id: null,
    brandName: "",
    description: "",
    logoUrl: "",
    tags: "",
    status: "ENABLED",
    companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
  });
  const [brands, setBrands] = useState([]);
  const [editableBrands, setEditableBrands] = useState([]);
  const { user } = useAuth();

  // ---- Sidebar collapse (whole aside) — persisted so it stays how the
  // user left it across reloads -------------------------------------
  const SIDEBAR_COLLAPSED_KEY = "brands_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable (e.g. private mode) — fail silently
      }
      return next;
    });
  };

  // ---- "How It Works" card collapse — independent of the sidebar
  // collapse above, also persisted -------------------------------------
  const INFO_CARD_COLLAPSED_KEY = "brands_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable — fail silently
      }
      return next;
    });
  };

  // Derived, real-data KPI stats (no fabricated numbers)
  const brandStats = useMemo(() => {
    const total = brands.length;
    const active = brands.filter((b) => b.status === "ENABLED").length;
    const disabled = brands.filter((b) => b.status === "DISABLED").length;
    const tagged = brands.filter(
      (b) => b.tags && b.tags.toString().trim() !== "",
    ).length;
    return { total, active, disabled, tagged };
  }, [brands]);

  const getApiBaseUrl = useCallback(() => {
    if (user?.authorities?.includes("ROLE_ERP-ADMIN")) {
      return process.env.REACT_APP_API_ERP_ADMIN_URL;
    }
    if (user?.authorities?.includes("ROLE_INVENTORY-MANAGER")) {
      return process.env.REACT_APP_API_INV_MANAGER_URL;
    }
    return process.env.REACT_APP_API_CRM_ADMIN_URL;
  }, [user?.authorities]);

  const apiBaseUrl = getApiBaseUrl();

  // Fetch brands data
  const fetchBrands = useCallback(async () => {
    const apiBaseUrl = getApiBaseUrl();
    try {
      const response = await axiosInstance.get(`${apiBaseUrl}/brands`);
      const brandsData =
        response.data?.data?.map((brand) => ({
          id: brand.id || "N/A",
          brandName: brand.brandName || "Unknown",
          description: brand.description || "",
          logoUrl: brand.logoUrl || "",
          tags: brand.tags || "",
          status: brand.status?.toUpperCase() || "ENABLED",
          companyName: brand.companyName || "N/A",
        })) || [];

      setBrands(brandsData);
      setEditableBrands(brandsData.map((brand) => ({ ...brand })));

      if (brandsData.length > 0) {
        markPageAsCompleted("Brands");
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  }, [markPageAsCompleted, getApiBaseUrl]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handleSelectAll = () => {
    const allBrandIds = brands.map((brand) => brand.id);
    if (selectAll) {
      setSelectedBrands([]);
      setSelectAll(false);
    } else {
      setSelectedBrands(allBrandIds);
      setSelectAll(true);
    }
  };

  const tableColumns = [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <ColoredCheckbox
            name="select-all"
            color="green"
            checked={selectAll}
            onChange={handleSelectAll}
            value="all"
          />
          <span>Brand Name</span>
        </div>
      ),
      accessor: "brandName",
    },
    { header: "Description", accessor: "description" },
    { header: "Tags", accessor: "tags" },
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const brandValidationSchema = Yup.object().shape({
    brandName: Yup.string().required("Brand name is required"),
    description: Yup.string(),
    logoUrl: Yup.string().url("Must be a valid URL"),
    tags: Yup.string(),
  });

  const handleSelectBrand = (brandId) => {
    setSelectedBrands((prev) => {
      if (prev.includes(brandId)) {
        return prev.filter((id) => id !== brandId);
      } else {
        return [...prev, brandId];
      }
    });
  };

  useEffect(() => {
    const allBrandIds = brands.map((brand) => brand.id);
    const allSelected =
      allBrandIds.length > 0 &&
      allBrandIds.every((id) => selectedBrands.includes(id));
    setSelectAll(allSelected);
  }, [selectedBrands, brands]);

  const handleBatchDelete = () => {
    if (selectedBrands.length === 0) {
      toast.warning("Please select at least one brand to delete");
      return;
    }

    setPopupAction(() => async () => {
      try {
        const ids = selectedBrands.map((id) => Number(id));
        console.log(ids);

        await axiosInstance.delete(`${apiBaseUrl}/brands/bulk`, {
          data: ids,
        });

        toast.success(`Successfully deleted ${selectedBrands.length} brand(s)`);
        setSelectedBrands([]);
        setSelectAll(false);
        fetchBrands();
        markPageAsCompleted("Brands");
      } catch (error) {
        console.error("Error deleting brands:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });
    setShowWarningPopup(true);
  };

  const handleBulkUpdateClick = () => {
    if (bulkUpdateEnabled) {
      const hasEmptyName = editableBrands.some(
        (brand) => !brand.brandName || brand.brandName.trim() === "",
      );
      if (hasEmptyName) {
        toast.error("All brand names must be filled out before submitting.");
        return;
      }

      const payload = editableBrands.map((brand) => ({
        id: brand.id,
        brandName: brand.brandName,
        description: brand.description,
        logoUrl: brand.logoUrl,
        tags: brand.tags,
        status: brand.status,
        companyId:
          parseInt(process.env.REACT_APP_COMPANY_ID) || brand.companyId,
      }));

      axiosInstance
        .put(`${apiBaseUrl}/brands/bulk`, payload)
        .then(() => {
          toast.success("Bulk update submitted successfully");
          setBrands([...editableBrands]);
          markPageAsCompleted("Brands");
        })
        .catch((error) => {
          console.error("Error updating brands:", error);
        });
    }

    setBulkUpdateEnabled((prev) => !prev);
  };

  const handleInlineEdit = (brandId, field, value) => {
    setEditableBrands((prev) =>
      prev.map((brand) =>
        brand.id === brandId ? { ...brand, [field]: value } : brand,
      ),
    );
  };

  const handleEditBrand = (brandId) => {
    const brand = brands.find((b) => b.id === brandId);
    if (brand) {
      setIsEditing(true);
      setInitialValues(brand);
      setShowForm(true);
    }
  };

  const handleDelete = async (brandId) => {
    try {
      await axiosInstance.delete(`${apiBaseUrl}/brands/${brandId}`);
      toast.success("Brand deleted successfully.");
      fetchBrands();
      markPageAsCompleted("Brands");
    } catch (error) {
      console.error("Error deleting brand:", error);
    }
  };

  const toggleBrandStatus = async (brandId, currentStatus) => {
    const apiBaseUrl = getApiBaseUrl();
    const isCurrentlyEnabled = currentStatus === "ENABLED";
    const newStatus = isCurrentlyEnabled ? "DISABLED" : "ENABLED";

    // ✅ FIX: Construct the endpoint according to the new format.
    const action = isCurrentlyEnabled ? "disable" : "enable";
    const endpoint = `${apiBaseUrl}/brands/${action}/${brandId}`;

    try {
      await axiosInstance.put(endpoint);

      // Update the local state to reflect the change immediately
      const updateState = (prevState) =>
        prevState.map((brand) =>
          brand.id === brandId ? { ...brand, status: newStatus } : brand,
        );

      setBrands(updateState);
      setEditableBrands(updateState);

      toast.success(`Brand status changed to ${newStatus.toLowerCase()}`);
      markPageAsCompleted("Brands");
    } catch (error) {
      console.error(`Error changing status to ${newStatus}:`, error);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const apiBaseUrl = getApiBaseUrl();

      // Construct the payload for PUT/POST
      const payload = {
        id: values.id, // Required for PUT
        brandName: values.brandName,
        description: values.description,
        logoUrl: values.logoUrl,
        tags: values.tags,
        status: "ENABLED", // Default status
        companyId:
          parseInt(process.env.REACT_APP_COMPANY_ID) || values.companyId,
      };

      if (isEditing) {
        // PUT request: send entire brand object in body
        await axiosInstance.put(`${apiBaseUrl}/brands`, payload);
        toast.success("Brand updated successfully.");
      } else {
        // POST request: create new brand
        await axiosInstance.post(`${apiBaseUrl}/brands`, payload);
        toast.success("Brand created successfully.");
      }

      // Refresh the local brand list
      fetchBrands();

      // Reset form and close
      resetForm();
      setIsEditing(false);
      setShowForm(false);

      markPageAsCompleted("Brands");
    } catch (error) {
      console.error("Error submitting brand:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const transformedData = (bulkUpdateEnabled ? editableBrands : brands).map(
    (brand) => ({
      brandName: (
        <div className="brand-name-cell">
          <ColoredCheckbox
            name={`brand-${brand.id}`}
            color="green"
            checked={selectedBrands.includes(brand.id)}
            onChange={() => handleSelectBrand(brand.id)}
            value={brand.id}
          />
          <span className="brand-avatar" aria-hidden="true">
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt="" />
            ) : (
              (brand.brandName || "?").charAt(0).toUpperCase()
            )}
          </span>
          {bulkUpdateEnabled ? (
            <input
              type="text"
              value={brand.brandName}
              onChange={(e) =>
                handleInlineEdit(brand.id, "brandName", e.target.value)
              }
              className="inline-edit-input"
              style={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "4px 8px",
                fontSize: "14px",
                minWidth: "120px",
              }}
            />
          ) : (
            <span className="brand-name-text">{brand.brandName}</span>
          )}
        </div>
      ),
      description: bulkUpdateEnabled ? (
        <input
          type="text"
          value={brand.description}
          onChange={(e) =>
            handleInlineEdit(brand.id, "description", e.target.value)
          }
          className="inline-edit-input"
          style={{
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "4px 8px",
            fontSize: "14px",
            minWidth: "150px",
          }}
        />
      ) : (
        <span title={brand.description || "-"}>
          {brand.description
            ? brand.description.length > 50
              ? `${brand.description.substring(0, 50)}...`
              : brand.description
            : "-"}
        </span>
      ),
      tags: bulkUpdateEnabled ? (
        <input
          type="text"
          value={brand.tags}
          onChange={(e) => handleInlineEdit(brand.id, "tags", e.target.value)}
          className="inline-edit-input"
          style={{
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "4px 8px",
            fontSize: "14px",
            minWidth: "100px",
          }}
        />
      ) : brand.tags ? (
        <div className="brand-tags-cell">
          {brand.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean)
            .map((tag, idx) => (
              <span className="tag-pill" key={`${brand.id}-tag-${idx}`}>
                {tag}
              </span>
            ))}
        </div>
      ) : (
        <span>-</span>
      ),
      status: (
        <div className="brand-status-cell">
          <ToggleButton
            isOn={brand.status === "ENABLED"}
            onToggle={() => toggleBrandStatus(brand.id, brand.status)}
            status={brand.status === "DISABLED"}
          />
          <span
            className={`brand-status-caption ${
              brand.status === "ENABLED" ? "is-enabled" : "is-disabled"
            }`}
          >
            {brand.status === "ENABLED"
              ? "Available for use"
              : "Hidden from product selection"}
          </span>
        </div>
      ),
      actions: (
        <div style={{ display: "flex", gap: "8px" }}>
          <IconButton
            icon="iconamoon:edit-light"
            onClick={() => handleEditBrand(brand.id)}
            variant="edit"
            title="Edit Brand"
          />
          <IconButton
            icon="fluent:delete-28-regular"
            onClick={() => handleDelete(brand.id)}
            variant="delete"
            title="Delete Brand"
          />
        </div>
      ),
      originalBrand: brand,
      searchableText: `${brand.brandName} ${brand.description} ${brand.tags} ${brand.status}`,
    }),
  );

  return (
    <div className="brands">
      {/* KPI summary cards - derived from real brand data */}
      <div className="brands__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Brands</span>
            <span className="metric-tile__value">{brandStats.total}</span>
          </div>

          <div className="metric-tile__icon metric-tile__icon--neutral">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Active Brands</span>
            <span className="metric-tile__value">{brandStats.active}</span>
          </div>

          <div className="metric-tile__icon metric-tile__icon--success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m5 13 4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Disabled Brands</span>
            <span className="metric-tile__value">{brandStats.disabled}</span>
          </div>

          <div className="metric-tile__icon metric-tile__icon--warning">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="m9 9 6 6M15 9l-6 6" />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Tagged Brands</span>
            <span className="metric-tile__value">{brandStats.tagged}</span>
          </div>

          <div className="metric-tile__icon metric-tile__icon--info">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20.6 12.6 12.6 4.6a2 2 0 0 0-1.4-.6H5a1 1 0 0 0-1 1v6.2a2 2 0 0 0 .6 1.4l8 8a2 2 0 0 0 2.8 0l5.2-5.2a2 2 0 0 0 0-2.8Z"
              />
              <circle cx="8" cy="8" r="1" />
            </svg>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="brands__tip-banner">
        <span className="brands__tip-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.3V16h6v-1.2c0-.9.4-1.7 1-2.3A6 6 0 0 0 12 2Z"
            />
          </svg>
        </span>
        <p>
          <strong>Tip:</strong> Use clear and consistent brand names. Disabling
          a brand hides it from product selection but does not delete existing
          product data.
        </p>
      </div>

      <div className="brands__actions">
        <button
          type="button"
          className={`action-card ${showForm ? "action-card--active" : ""}`}
          onClick={() => {
            if (showForm) {
              setShowForm(false);
            } else {
              setInitialValues({
                id: null,
                brandName: "",
                description: "",
                logoUrl: "",
                tags: "",
                status: "ENABLED",
                companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
              });
              setShowForm(true);
              setIsEditing(false);
            }
          }}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={showForm ? "M6 18 18 6M6 6l12 12" : "M12 4v16m8-8H4"}
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {showForm ? "Close Brand Form" : "Create Brand"}
            </span>
            <span className="action-card-desc">
              {showForm
                ? "Hide the brand form"
                : "Add a new brand to get started"}
            </span>
          </span>
        </button>

        {/* Bulk Update Toggle/Submit */}
        <button
          type="button"
          className={`action-card ${bulkUpdateEnabled ? "action-card--success" : ""}`}
          onClick={handleBulkUpdateClick}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {bulkUpdateEnabled ? "Submit Bulk Update" : "Enable Bulk Update"}
            </span>
            <span className="action-card-desc">
              {bulkUpdateEnabled
                ? "Save changes made to multiple brands"
                : "Edit multiple brands"}
            </span>
          </span>
        </button>

        {bulkUpdateEnabled && (
          <button
            type="button"
            className="action-card action-card--danger-outline"
            onClick={() => {
              setBulkUpdateEnabled(false);
              setEditableBrands([...brands]); // revert changes
            }}
          >
            <span className="action-card-copy">
              <span className="action-card-title">Cancel Bulk Update</span>
              <span className="action-card-desc">Discard unsaved changes</span>
            </span>
          </button>
        )}
        <button
          type="button"
          className={`action-card action-card--danger-outline ${
            selectedBrands.length === 0 ? "action-card--disabled" : ""
          }`}
          onClick={handleBatchDelete}
          disabled={selectedBrands.length === 0}
        >
          <span className="action-card-copy">
            <span className="action-card-title">
              Bulk Delete ({selectedBrands.length})
            </span>
            <span className="action-card-desc">
              Permanently delete all selected brands
            </span>
          </span>
        </button>
      </div>

      <div
        className={`brands__layout ${
          isSidebarCollapsed ? "brands__layout--collapsed" : ""
        }`}
      >
        <div className="brands__main">
          {showForm && (
            <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={brandValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values, setFieldValue }) => (
                  <Form>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                      {isEditing ? "Edit Brand" : "Create New Brand"}
                    </h3>

                    <div className="brand-form__grid">
                      {/* Row 1: Brand Name + Logo */}
                      <div className="brand-form-group">
                        <label className="label">Brand Name</label>
                        <Field
                          type="text"
                          name="brandName"
                          className="form-input"
                          placeholder="Brand Name"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            This is how the brand appears across products and
                            reports.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="brandName"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      <div className="brand-form-group">
                        <label className="label">Logo URL</label>
                        <Field
                          type="text"
                          name="logoUrl"
                          className="form-input"
                          placeholder="https://example.com/logo.png"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            Optional. Shown as the brand's avatar in the table.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="logoUrl"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      {/* Row 2: Description (full width) */}
                      <div className="brand-form-group description-col">
                        <label className="label">Description</label>
                        <Field
                          as="textarea"
                          name="description"
                          className="form-input"
                          placeholder="Brand description"
                          rows="3"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            A short summary shown when browsing brands.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="description"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      {/* Row 3: Tags (full width) */}
                      <div className="brand-form-group tags-col">
                        <label className="label">Tags</label>
                        <Field
                          type="text"
                          name="tags"
                          className="form-input"
                          placeholder="Electronics, Premium, Tech"
                        />
                        <div className="helper-placeholder">
                          <span className="helper-text">
                            Separate multiple tags with commas. Used for
                            filtering and search.
                          </span>
                        </div>
                        <div className="error-placeholder">
                          <ErrorMessage
                            name="tags"
                            component="div"
                            className="error-message"
                          />
                        </div>
                      </div>

                      {/* Row 4: Submit button */}
                      <div className="brand-form-group button-group">
                        <ButtonWithIcon
                          type="submit"
                          icon="tdesign:send"
                          text={isEditing ? "Update Brand" : "Create Brand"}
                          disabled={isSubmitting}
                          className={
                            isEditing ? "edit-button" : "submit-button"
                          }
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          <div className="brands__table-section">
            {brandStats.total === 0 ? (
              <div className="brands__empty-state">
                <svg
                  className="brands__empty-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.6 12.6 12.6 4.6a2 2 0 0 0-1.4-.6H5a1 1 0 0 0-1 1v6.2a2 2 0 0 0 .6 1.4l8 8a2 2 0 0 0 2.8 0l5.2-5.2a2 2 0 0 0 0-2.8Z"
                  />
                  <circle cx="8" cy="8" r="1" />
                </svg>
                <h3>No brands found</h3>
                <p>
                  Brands help you organize products and keep your inventory
                  consistent. To get started:
                </p>
                <ol className="brands__empty-steps">
                  <li>Create your first brand</li>
                  <li>Enable it so it becomes available</li>
                  <li>Assign it to your products</li>
                </ol>
                <button
                  type="button"
                  className="action-card action-card--primary"
                  onClick={() => setShowForm(true)}
                >
                  <span className="action-card-copy">
                    <span className="action-card-title">Create Brand</span>
                    <span className="action-card-desc">
                      Add your first brand
                    </span>
                  </span>
                </button>
              </div>
            ) : (
              <GeneralTable
                title="Brands"
                columns={tableColumns}
                data={transformedData}
                selectedCount={selectedBrands.length}
                showPagination={true}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          className="brands__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside
          className={`brands__sidebar ${
            isSidebarCollapsed ? "brands__sidebar--collapsed" : ""
          }`}
          aria-hidden={isSidebarCollapsed}
        >
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>

            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Create Brand</strong>
                    <span>
                      Add a new brand with name, description and tags.
                    </span>
                  </li>
                  <li>
                    <strong>Assign to Products</strong>
                    <span>
                      Select the brand while creating or editing products.
                    </span>
                  </li>
                  <li>
                    <strong>Use Everywhere</strong>
                    <span>
                      The brand becomes available in inventory, records and
                      reports.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Use unique and clear brand names</li>
              <li>Enable only the brands currently in use</li>
              <li>Use tags to make brands easier to filter</li>
              <li>Review and clean up unused brands regularly</li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Deleting a brand is permanent and cannot be undone. Products using
              a deleted brand may need to be reassigned.
            </p>
          </div>
        </aside>
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => popupAction()}
        onDecline={() => setShowWarningPopup(false)}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedBrands.length} selected brand${
          selectedBrands.length === 1 ? "" : "s"
        }? This action is permanent and cannot be undone. Products currently using ${
          selectedBrands.length === 1 ? "this brand" : "these brands"
        } may need to be reassigned.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default Brands;
